import React, { useEffect, useState } from "react";
import { Spin, Row, Col, Space, Card, Typography, Select } from "antd";
import { Medal } from "lucide-react";
import { FONT, ROLE_USER, USER_STORAGE } from "constants/index";
import { notificationErroInternal } from "util/api-utils";
import { ColumnsUser } from "./columns-table";
import useBreakpoint from "hooks/useBreakpoint";
import ResponsiveDataList from "components/responsive-data-list";
import { listMinWidth } from "util/responsive";
import {
  getUsersReviewsRanking,
  getUsersReviewsRankingSprint,
} from "services/review/review-service";
import { useSelector } from "react-redux";
import { selectRole } from "reducer/login/login-user";

const { Text } = Typography;

const RANGE_AGE_OPTIONS = [
  { value: "GENERAL", label: "Geral" },
  { value: "ELEVEN_MINUS", label: "<11 anos" },
  { value: "TWELVE_FOURTEEN", label: "12 a 14 anos" },
  { value: "FIFTEEN_EIGHTEEN", label: "15 a 18 anos" },
  { value: "NINETEEN_PLUS", label: ">19 anos" },
];

const GENDER_OPTIONS = [
  { value: "BOTH", label: "Ambos" },
  { value: "MALE", label: "Masculino" },
  { value: "FEMALE", label: "Feminino" },
];

const SPRINT_TYPE_OPTIONS = [
  { value: "SPRINT_LINEAR", label: "Sprint Linear" },
  { value: "COD_SPRINT", label: "Cod Sprint" },
];

const RankingReview = (props) => {
  const { isMobile } = useBreakpoint();
  const [loading, setLoading] = useState(true);
  const [rangeAge, setRangeAge] = useState("GENERAL");
  const [data, setData] = useState([]);
  const [roleRedux, setRoleRedux] = useState(useSelector(selectRole));
  const [idUser, setIdUser] = useState(null);
  const [field, setField] = useState("point");
  const [titleField, setTitleField] = useState("Avaliação");
  const [sprintType, setSprintType] = useState(null);
  const [gender, setGender] = useState("BOTH");

  useEffect(() => {
    switch (props.type) {
      case "REVIEW":
        setField("point");
        setTitleField("Avaliação");
        break;
      case "CMJ":
        setField("cmj");
        setTitleField("Salto");
        break;
      case "SPRINT":
        setField("sprintLinear");
        setTitleField("Tempo");
        break;
      default:
        break;
    }
    refreshData(rangeAge, sprintType, gender);
  }, []);

  useEffect(() => {
    const localStorageItem = localStorage.getItem(USER_STORAGE);
    if (localStorageItem != null) {
      const { role, id } = JSON.parse(localStorageItem);
      if (roleRedux == "" || role != roleRedux) {
        setRoleRedux(role);
      }
      setIdUser(id);
    }
  }, [localStorage.getItem(USER_STORAGE)]);

  function refreshData(rangeAge, sprintType, gender) {
    setLoading(true);
    setRangeAge(rangeAge);
    setSprintType(sprintType);
    setGender(gender);
    switch (sprintType) {
      case "SPRINT_LINEAR":
        setField("sprintLinear");
        break;
      case "COD_SPRINT":
        setField("codSprint");
        break;
      default:
        break;
    }
    getData(rangeAge, sprintType, gender);
  }

  function getData(rangeAge, sprintType, gender) {
    let obj = {
      rangeAge: rangeAge,
      rankingType: props.type,
      sprintType: sprintType ?? "SPRINT_LINEAR",
      gender: gender,
    };

    const cleanParam = Object.fromEntries(
      Object.entries(obj).filter(([key, value]) => value !== undefined)
    );

    var funcExecute = getUsersReviewsRanking;
    if (props.type == "SPRINT") {
      var funcExecute = getUsersReviewsRankingSprint;
    }
    funcExecute(cleanParam)
      .then((res) => {
        setLoading(false);
        setData(res.map((item, index) => ({ ...item, rank: index + 1 })));
      })
      .catch(() => {
        setLoading(false);
        notificationErroInternal(
          "Aconteceu um erro inesperado, por favor, tente mais tarde."
        );
      });
  }

  const mountFilter = () => (
    <Row
      className="reviews-filter-row"
      gutter={[16, 16]}
      style={{ padding: "10px", alignItems: "center" }}
    >
      {roleRedux != ROLE_USER && (
        <>
          <Col xs={24} md={12} lg={8}>
            <Select
              value={rangeAge}
              placeholder="Faixa Etária"
              options={RANGE_AGE_OPTIONS}
              onChange={(value) => refreshData(value, sprintType, gender)}
              style={{ width: "100%" }}
            />
          </Col>
          <Col xs={24} md={12} lg={8}>
            <Select
              value={gender}
              placeholder="Gênero"
              options={GENDER_OPTIONS}
              onChange={(value) => refreshData(rangeAge, sprintType, value)}
              style={{ width: "100%" }}
            />
          </Col>
        </>
      )}
      {props.type == "SPRINT" && (
        <Col xs={24} md={12} lg={8}>
          <Select
            value={sprintType ?? "SPRINT_LINEAR"}
            placeholder="Sprint"
            options={SPRINT_TYPE_OPTIONS}
            onChange={(value) => refreshData(rangeAge, value, gender)}
            style={{ width: "100%" }}
          />
        </Col>
      )}
    </Row>
  );

  return (
    <div className="container-ranking-client">
      <Spin size="large" spinning={loading}>
        {mountFilter()}
        <Row gutter={[24, 24]} style={{ marginLeft: "0px" }}>
          <Space
            direction="vertical"
            size={"small"}
            style={{
              flex: "1 1 500px",
              minWidth: listMinWidth(isMobile),
            }}
          >
            <Card
              id="card-table"
              className="card-table-list"
              title={
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div style={{ flex: 1 }}>
                    <Medal
                      size={18}
                      style={{ marginBottom: "-4px", marginRight: "10px" }}
                    />
                    <Text style={{ fontSize: "15px", fontFamily: FONT }}>
                      Classificação dos Alunos
                    </Text>
                  </div>
                </div>
              }
              bordered={false}
            >
              <ResponsiveDataList
                isMobile={isMobile}
                forceCardLayout
                cardVariant="ranking"
                rowKey="id"
                columns={ColumnsUser(titleField, field)}
                dataSource={data}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: false,
                }}
                locale={{
                  emptyText: "Sem registro",
                }}
                rowClassName={(record) => {
                  return record.id === idUser && roleRedux == ROLE_USER
                    ? "highlight-row"
                    : "";
                }}
              />
            </Card>
          </Space>
        </Row>
      </Spin>
    </div>
  );
};
export default RankingReview;
